import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { SelectField } from "../../components/ui/select-field.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Pagination } from "../../components/ui/pagination.js";

type Status = "SENT" | "FAILED" | "SKIPPED";

interface EmailLogRow {
  id: string; type: string; toAddress: string; fromAddress: string | null; subject: string | null;
  status: Status; reason: string | null; errorCode: string | null; messageId: string | null;
  isTest: boolean; createdAt: string;
}
interface EmailLogResponse {
  data: EmailLogRow[];
  meta: { total: number; page: number; limit: number };
  summary: Record<Status, number>;
}

const STATUS_TONE: Record<Status, "success" | "error" | "warning"> = { SENT: "success", FAILED: "error", SKIPPED: "warning" };
const STATUS_LABEL: Record<Status, string> = { SENT: "Sent", FAILED: "Failed", SKIPPED: "Not sent" };
const LIMIT = 25;

/** Every email the app tried to send, with the exact reason when one failed or was skipped. */
export default function EmailDeliveryLog({ refreshKey }: { refreshKey: number }) {
  const [rows, setRows] = useState<EmailLogRow[]>([]);
  const [summary, setSummary] = useState<Record<Status, number>>({ SENT: 0, FAILED: 0, SKIPPED: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"" | Status>("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Debounce the recipient search so each keystroke doesn't hit the API.
  useEffect(() => {
    const t = window.setTimeout(() => { setQuery(search.trim()); setPage(1); }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (status) params.set("status", status);
      if (query) params.set("search", query);
      const r = await api.get<EmailLogResponse>(`/admin/email-logs?${params.toString()}`);
      setRows(r.data.data); setTotal(r.data.meta.total); setSummary(r.data.summary);
    } catch (err) {
      logError("EmailDeliveryLog.load", err);
      setError(errMsg(err) ?? "Couldn't load the delivery log.");
    } finally { setLoading(false); }
  }, [page, status, query]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const pickStatus = (s: "" | Status) => { setStatus(s); setPage(1); };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Delivery log</CardTitle>
          <Button size="sm" variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="size-3.5" /> Refresh
          </Button>
        </div>
        <CardDescription>
          Every email the system tried to send, newest first. "Failed" shows Amazon SES's exact error; "Not sent" shows why it
          never left (email switched off, template inactive, no address on record).
        </CardDescription>
        <div className="mt-2 flex flex-wrap gap-2">
          {(["SENT", "FAILED", "SKIPPED"] as const).map(s => (
            <button key={s} type="button" onClick={() => pickStatus(status === s ? "" : s)}
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              aria-pressed={status === s} title={status === s ? "Show all" : `Show only ${STATUS_LABEL[s].toLowerCase()}`}>
              <Badge tone={status === s ? STATUS_TONE[s] : "outline"}>{STATUS_LABEL[s]}: {summary[s]}</Badge>
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <SearchInput className="w-64" placeholder="Search recipient email" value={search}
            onChange={e => setSearch(e.target.value)} aria-label="Search recipient email" />
          <SelectField size="sm" className="w-40" value={status} onChange={e => pickStatus(e.target.value as "" | Status)}>
            <option value="">All outcomes</option>
            <option value="SENT">Sent</option>
            <option value="FAILED">Failed</option>
            <option value="SKIPPED">Not sent</option>
          </SelectField>
        </div>

        {error && <p className="m-0 text-[12px] text-error">{error}</p>}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-6 text-center text-surface-400">No emails recorded yet.</TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} className="align-top">
                  <TableCell className="whitespace-nowrap text-[12px] text-surface-600">
                    {new Date(r.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </TableCell>
                  <TableCell className="min-w-[180px]">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="font-mono text-[12px]">{r.type}</span>
                      {r.isTest && <Badge tone="info">Test</Badge>}
                    </div>
                    {r.subject && <div className="text-[11px] text-surface-500">{r.subject}</div>}
                  </TableCell>
                  <TableCell className="text-[12px]">{r.toAddress}</TableCell>
                  <TableCell><Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge></TableCell>
                  <TableCell className="min-w-[240px] text-[12px]">
                    {r.status === "SENT" ? (
                      <span className="text-surface-400" title="Amazon SES message ID">
                        {r.messageId ? `SES ID ${r.messageId}` : "Delivered to SES"}
                      </span>
                    ) : (
                      <span className="break-words text-surface-700">
                        {r.errorCode && <span className="font-mono font-semibold">{r.errorCode}: </span>}
                        {r.reason ?? "No reason recorded"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {total > LIMIT && (
          <Pagination meta={{ total, page, limit: LIMIT, totalPages: Math.ceil(total / LIMIT) }} onPageChange={setPage} />
        )}
      </CardContent>
    </Card>
  );
}
